import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session Founder normale (JWT existant) pour le bootstrap (enroll TOTP, register passkey).
const founderToken = () => localStorage.getItem("token");
const rootToken = () => localStorage.getItem("root_token");

const founderApi = () =>
  axios.create({ baseURL: API, headers: { Authorization: `Bearer ${founderToken()}` } });

export const rootApi = () =>
  axios.create({ baseURL: API, headers: { Authorization: `Bearer ${rootToken()}` } });

export const hasRootSession = () => !!rootToken();
export const clearRootSession = () => localStorage.removeItem("root_token");

// ---- WebAuthn base64url helpers ----
const b64uToBuf = (s) => {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0)).buffer;
};
const bufToB64u = (b) =>
  btoa(String.fromCharCode(...new Uint8Array(b)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const serialize = (c) => {
  const r = c.response;
  return {
    id: c.id, rawId: bufToB64u(c.rawId), type: c.type,
    response: {
      clientDataJSON: bufToB64u(r.clientDataJSON),
      attestationObject: r.attestationObject ? bufToB64u(r.attestationObject) : undefined,
      authenticatorData: r.authenticatorData ? bufToB64u(r.authenticatorData) : undefined,
      signature: r.signature ? bufToB64u(r.signature) : undefined,
      userHandle: r.userHandle ? bufToB64u(r.userHandle) : null,
      transports: r.getTransports ? r.getTransports() : [],
    },
    clientExtensionResults: c.getClientExtensionResults(),
  };
};

export const getRootStatus = async () => (await founderApi().get("/root/status")).data;

export const enrollTotp = async () => (await founderApi().post("/root/totp/enroll")).data;

export const confirmTotp = async (code) =>
  (await founderApi().post("/root/totp/confirm", { code })).data;

export const registerPasskey = async () => {
  const { data } = await founderApi().post("/root/passkey/register/options");
  data.challenge = b64uToBuf(data.challenge);
  data.user.id = b64uToBuf(data.user.id);
  (data.excludeCredentials || []).forEach((x) => (x.id = b64uToBuf(x.id)));
  const cred = await navigator.credentials.create({ publicKey: data });
  return (await founderApi().post("/root/passkey/register/verify", { credential: serialize(cred) })).data;
};

export const loginRoot = async (code) => {
  const { data } = await axios.post(`${API}/root/passkey/options`, {});
  data.challenge = b64uToBuf(data.challenge);
  (data.allowCredentials || []).forEach((x) => (x.id = b64uToBuf(x.id)));
  const cred = await navigator.credentials.get({ publicKey: data });
  const res = await axios.post(`${API}/root/verify`, { credential: serialize(cred), code });
  localStorage.setItem("root_token", res.data.access_token);
  return res.data;
};

// ---- Console (require_root) ----
export const rootConverse = async (message) => (await rootApi().post("/tuluss/root/converse", { message })).data;
export const rootAnalyze = async (problem, importance = "normal") =>
  (await rootApi().post("/tuluss/root/analyze", { problem, importance })).data;
export const rootFractal = async (problem, importance = "high") =>
  (await rootApi().post("/tuluss/root/fractal", { problem, importance })).data;
export const rootMemory = async () => (await rootApi().get("/tuluss/root/memory")).data;
export const rootApprovals = async () => (await rootApi().get("/tuluss/root/approvals")).data;
export const rootHealth = async () => (await rootApi().get("/tuluss/root/health")).data;

// ---- PROD 9 : Biometric Identity Fabric + Step-up (WebAuthn réel) ----
export const getBiometricStatus = async () =>
  (await rootApi().get("/tuluss/root/biometric/status")).data;

export const analyzeUnknown = async (demand) =>
  (await rootApi().post("/tuluss/root/cognition/unknown", { demand })).data;

export const analyzeFractalExponential = async (demand, importance = "normal", uncertainty = 0.5) =>
  (await rootApi().post("/tuluss/root/cognition/fractal-exponential",
    { demand, importance, uncertainty })).data;

export const getReadiness = async () => (await rootApi().get("/tuluss/root/readiness")).data;

// Step-up : réauthentification forte fraîche (passkey natif appareil + TOTP) liée à une action.
export const stepUp = async (action, code) => {
  const { data } = await rootApi().post("/root/stepup/options", {});
  data.challenge = b64uToBuf(data.challenge);
  (data.allowCredentials || []).forEach((x) => (x.id = b64uToBuf(x.id)));
  const cred = await navigator.credentials.get({ publicKey: data });
  const res = await rootApi().post("/root/stepup/verify", {
    credential: serialize(cred), code, action,
  });
  return res.data.step_up_token;
};

// Action critique protégée par step-up : ajouter un appareil de confiance.
export const addTrustedDevice = async (deviceId, label, stepUpToken) =>
  (await rootApi().post("/tuluss/root/biometric/device",
    { device_id: deviceId, label },
    { headers: { "X-Root-Stepup": stepUpToken } })).data;

// ---- ÉTAPE 3 : Tuluss = interface admin principale + Fabric LIVE ----
export const adminConverse = async (message, args = null) =>
  (await rootApi().post("/tuluss/root/admin/converse", { message, args })).data;
export const getCapabilities = async () =>
  (await rootApi().get("/tuluss/root/admin/capabilities")).data;
export const getAdminActions = async () =>
  (await rootApi().get("/tuluss/root/admin/actions")).data;
export const getFabricStatus = async () =>
  (await rootApi().get("/tuluss/root/fabric/status")).data;
export const getDeadLetters = async () =>
  (await rootApi().get("/tuluss/root/fabric/dead-letters")).data;
export const replayEvent = async (eventId) =>
  (await rootApi().post(`/tuluss/root/fabric/replay/${eventId}`)).data;
export const listApprovals = async () =>
  (await rootApi().get("/tuluss/root/approvals")).data;
export const approveAction = async (id) =>
  (await rootApi().post(`/tuluss/root/approvals/${id}/approve`)).data;
export const rejectAction = async (id, reason = "") =>
  (await rootApi().post(`/tuluss/root/approvals/${id}/reject`, { reason })).data;

// ---- ÉTAPE 4 : Gouverneur cognitif proactif 24/7 ----
export const runGovernor = async () =>
  (await rootApi().post("/tuluss/root/governor/run")).data;
export const getProposals = async () =>
  (await rootApi().get("/tuluss/root/governor/proposals")).data;
export const getGovernorInbox = async () =>
  (await rootApi().get("/tuluss/root/governor/inbox")).data;

// ---- ÉTAPE 5 : systèmes LIVE + capacités + TOP 3 Founder ----
export const getFabricSystems = async () =>
  (await rootApi().get("/tuluss/root/fabric/systems")).data;
export const getFounderTop = async () =>
  (await rootApi().get("/tuluss/root/governor/top")).data;

// ---- SUPERCOCKPIT Founder (lecture seule) ----
export const cockpitLive = async () =>
  (await rootApi().get("/tuluss/root/cockpit/live")).data;
export const cockpitPerformance = async (hours = 24) =>
  (await rootApi().get("/tuluss/root/cockpit/performance", { params: { hours } })).data;
export const cockpitLedger = async (limit = 50, offset = 0) =>
  (await rootApi().get("/tuluss/root/cockpit/ledger", { params: { limit, offset } })).data;
export const cockpitDecision = async (id) =>
  (await rootApi().get(`/tuluss/root/cockpit/decision/${id}`)).data;
export const cockpitDecisions = async (params = {}) =>
  (await rootApi().get("/tuluss/root/cockpit/decisions", { params })).data;
export const cockpitGps = async (resourceId, since, until, maxPoints = 1000) =>
  (await rootApi().get("/tuluss/root/cockpit/gps",
    { params: { resource_id: resourceId, since, until, max_points: maxPoints } })).data;
export const cockpitTimeline = async (params = {}) =>
  (await rootApi().get("/tuluss/root/cockpit/timeline", { params })).data;

// INCRÉMENT 3 — Control Lab / DJ Mixer (Root-gated, réutilise le moteur d'expérimentation)
export const mixerParams = async () =>
  (await rootApi().get("/tuluss/root/cockpit/mixer/params")).data;
export const cockpitExperiments = async (params = {}) =>
  (await rootApi().get("/tuluss/root/cockpit/experiments", { params })).data;
export const cockpitExperiment = async (id) =>
  (await rootApi().get(`/tuluss/root/cockpit/experiments/${id}`)).data;
export const cockpitDecisionChallenger = async (id) =>
  (await rootApi().get(`/tuluss/root/cockpit/decision/${id}/challenger`)).data;
export const mixerPropose = async (paramKey, challengerValue, reason) =>
  (await rootApi().post("/tuluss/root/cockpit/mixer/propose",
    { param_key: paramKey, challenger_value: challengerValue, reason })).data;
export const expAction = async (id, action, body = {}) =>
  (await rootApi().post(`/tuluss/root/cockpit/experiments/${id}/${action}`, body)).data;
export const cockpitAttention = async () =>
  (await rootApi().get("/tuluss/root/cockpit/attention")).data;
export const cockpitMeta = async () =>
  (await rootApi().get("/tuluss/root/cockpit/meta")).data;

// ---- Moteur IA de Tuluss (Founder-only, JWT) ----
export const aiGetConfig = async () => (await founderApi().get("/tuluss/ai/config")).data;
export const aiSelect = async (body) => (await founderApi().post("/tuluss/ai/select", body)).data;
export const aiTest = async (body) => (await founderApi().post("/tuluss/ai/test", body)).data;
export const aiReset = async () => (await founderApi().post("/tuluss/ai/reset")).data;

// ---- Flotte IA (A1) : Champion/fallbacks/SHADOW (Founder JWT) ----
export const fleetStatus = async () => (await founderApi().get("/tuluss/ai/fleet")).data;
export const fleetSetModels = async (model_ids) => (await founderApi().post("/tuluss/ai/fleet/models", { model_ids })).data;
export const fleetHealth = async () => (await founderApi().post("/tuluss/ai/fleet/health")).data;
export const fleetShadow = async () => (await founderApi().post("/tuluss/ai/fleet/shadow")).data;

// ---- Registre connecteurs (C) ----
export const connectorsStatus = async () => (await founderApi().get("/tuluss/connectors")).data;
export const connectorsHealth = async () => (await founderApi().post("/tuluss/connectors/health")).data;

// ---- GESTION Founder (JWT admin) : Livreurs & Partenaires ----
export const listDrivers = async () => (await founderApi().get("/partners/drivers")).data;
export const createDriver = async (body) => (await founderApi().post("/partners/drivers", body)).data;
export const updateDriver = async (id, body) => (await founderApi().put(`/partners/drivers/${id}`, body)).data;

export const listPartners = async () => (await founderApi().get("/partners/restaurants")).data;
export const createPartner = async (body) => (await founderApi().post("/partners/restaurants", body)).data;
export const updatePartner = async (id, body) => (await founderApi().put(`/partners/restaurants/${id}`, body)).data;

// ---- GESTION Founder : Clients (Phase B) ----
export const listClients = async (q = "") => (await founderApi().get(`/admin/clients?q=${encodeURIComponent(q)}`)).data;
export const clientDetail = async (id) => (await founderApi().get(`/admin/clients/${id}`)).data;
export const suspendClient = async (id) => (await founderApi().post(`/admin/clients/${id}/suspend`)).data;
export const reactivateClient = async (id) => (await founderApi().post(`/admin/clients/${id}/reactivate`)).data;
